import { useCallback, useEffect, useMemo, useState } from 'react';
import { getEventMentionArtists } from '../../utils/eventMentionParts';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useArtistEventChatParticipants(context) {
  const {
    ADMIN_PROFILE_IMAGE_PATH,
    API_URL,
    ARTIST_PROFILE_IMAGE_PATH,
    CHAT_MODE_GROUP_UNIFIED,
    appendVariant,
    buildAssetUrl,
    buildChatMentionCandidates,
    buildEventAdminParticipants,
    buildEventAdminProfilePayload,
    buildEventArtistParticipants,
    buildEventArtistProfilePayload,
    buildEventConciergeParticipants,
    buildEventParticipantEventMembershipLabel,
    buildEventParticipantEventPartSummary,
    buildEventScheduleFromInfo,
    buildFlyerUrl,
    buildGroupEventDetailRows,
    buildHeaderNextMultiPerformance,
    buildHeaderNextPerformance,
    buildUnifiedEventHeaderSchedule,
    createEventParticipantsPanelProps,
    events,
    fetchJsonSafe,
    formatDateJP,
    groupEventParticipantsByPart,
    groupEventSidebarRows,
    groupPanelEvents,
    loggedInArtistId,
    navigate,
    normalizeEventUuidList,
    normalizeGroupEventRows,
    normalizeMentionTargetId,
    normalizeMentionTargetRole,
    normalizeMentionTargetType,
    normalizePartLabel,
    parseVisibilityFlag,
    participantSnapshot,
    pickFirstString,
    resolveEventChatMode,
    resolveEventGroupCount,
    resolveEventScopedPartMeta,
    resolveEventThreadId,
    resolveEventVisibilityFromGroup,
    resolveHeaderFlyerSource,
    selectedEventId,
    selectedEventInfo,
    setIsMemberModalOpen,
    sidebarDetails,
    toDateInput,
    toTimeInput,
    useChatProfileModalState,
    useEventParticipantPartScope,
  } = context;
    const selectedEventAdmins = useMemo(() => {
      if (!selectedEventInfo || !Array.isArray(selectedEventInfo.assigned_admins)) return [];
      const seen = new Set();
      return selectedEventInfo.assigned_admins.filter((admin) => {
        if (!admin) return false;
        const rawKey = admin.admin_user_id ?? admin.id ?? admin.username ?? admin.display_name;
        if (rawKey === null || rawKey === undefined || rawKey === '') return true;
        const key = String(rawKey);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }, [selectedEventInfo]);
    const isConciergeEvent = useMemo(() => {
      if (!selectedEventInfo) return false;
      const flag = selectedEventInfo.is_concierge_event;
      if (typeof flag === 'boolean') return flag;
      const camelFlag = selectedEventInfo.isConciergeEvent;
      if (typeof camelFlag === 'boolean') return camelFlag;
      return Boolean(
        selectedEventInfo.concierge_application_id ||
        selectedEventInfo.concierge_application
      );
    }, [selectedEventInfo]);
    const currentEventMembersVisibleToArtists = useMemo(() => {
      if (participantSnapshot) {
        const rawCurrent =
          participantSnapshot.current_event_members_visible_to_artists ??
          participantSnapshot.currentEventMembersVisibleToArtists;
        if (rawCurrent !== undefined) {
          return parseVisibilityFlag(rawCurrent);
        }
        const fromGroup = resolveEventVisibilityFromGroup(participantSnapshot.group_events, selectedEventId);
        if (fromGroup !== undefined) return fromGroup;
      }
      if (!selectedEventInfo) return false;
      const raw =
        selectedEventInfo.members_visible_to_artists ??
        selectedEventInfo.membersVisibleToArtists;
      return parseVisibilityFlag(raw);
    }, [participantSnapshot, selectedEventId, selectedEventInfo]);
    const membersVisibleToArtists = useMemo(() => {
      if (participantSnapshot) {
        const rawSnapshot =
          participantSnapshot.members_visible_to_artists ??
          participantSnapshot.membersVisibleToArtists;
        if (rawSnapshot !== undefined) {
          return parseVisibilityFlag(rawSnapshot);
        }
        const groupEvents = Array.isArray(participantSnapshot.group_events)
          ? participantSnapshot.group_events
          : [];
        if (groupEvents.length > 0) {
          return groupEvents.some((event) => parseVisibilityFlag(event?.members_visible_to_artists));
        }
      }
      return currentEventMembersVisibleToArtists;
    }, [participantSnapshot, currentEventMembersVisibleToArtists]);
    const conciergePlanner = useMemo(() => {
      if (!isConciergeEvent) return null;
      const app = selectedEventInfo?.concierge_application;
      if (!app) return null;
      const name = pickFirstString(
        app.artist_name,
        app.name,
        app.display_name,
        app.full_name
      ) || '企画者';
      return {
        artist_id: app.artist_id ?? app.id ?? null,
        artist_name: name,
        artist_name_en: app.artist_name_en || app.name_en || '',
        artist_profile_picture: app.artist_profile_picture || app.profile_picture || '',
        artist_parts: app.artist_parts || app.parts || null,
        artist_gender: app.artist_gender || app.gender || null,
        profile_one_liner: app.profile_one_liner || '',
        instagram_url: app.instagram_url || '',
        twitter_url: app.twitter_url || '',
        facebook_url: app.facebook_url || '',
        hp_url: app.hp_url || '',
        career_timeline: Array.isArray(app.career_timeline)
          ? app.career_timeline
          : (Array.isArray(app.careerTimeline) ? app.careerTimeline : []),
      };
    }, [selectedEventInfo, isConciergeEvent]);
    const adminLookup = useMemo(() => {
      const map = new Map();
      selectedEventAdmins.forEach((admin) => {
        if (!admin) return;
        const id = admin.admin_user_id ?? admin.id ?? admin.user_id ?? admin.username;
        if (id == null) return;
        map.set(String(id), admin);
      });
      return map;
    }, [selectedEventAdmins]);
    const participantGroupEvents = useMemo(() => {
      const source = Array.isArray(participantSnapshot?.group_events)
        ? participantSnapshot.group_events
        : [];
      const normalized = source
        .map((event) => ({
          event_uuid: event?.event_uuid ? String(event.event_uuid) : '',
          name: pickFirstString(event?.name),
          performance_type: pickFirstString(event?.performance_type),
          event_date: event?.event_date || null,
          open_time: event?.open_time || null,
          start_time: event?.start_time || null,
          venue_name: pickFirstString(event?.venue_name, event?.venue, event?.place),
          venue: pickFirstString(event?.venue, event?.place),
          prefecture_name: pickFirstString(event?.prefecture_name, event?.prefecture),
          city_name: pickFirstString(event?.city_name, event?.city),
          venue_address: pickFirstString(event?.venue_address, event?.address),
          memo: pickFirstString(event?.memo, event?.note, event?.remarks),
          is_primary: event?.is_primary === true,
          members_visible_to_artists: parseVisibilityFlag(event?.members_visible_to_artists),
        }))
        .filter((event) => event.event_uuid);
      if (normalized.length > 0) return normalized;
      if (!selectedEventId && !selectedEventInfo?.event_uuid) return [];
      return [{
        event_uuid: String(selectedEventInfo?.event_uuid || selectedEventId),
        name: pickFirstString(selectedEventInfo?.name),
        performance_type: pickFirstString(selectedEventInfo?.performance_type),
        event_date: selectedEventInfo?.event_date || null,
        open_time: selectedEventInfo?.open_time || null,
        start_time: selectedEventInfo?.start_time || null,
        venue_name: pickFirstString(selectedEventInfo?.venue_name, selectedEventInfo?.venue, selectedEventInfo?.place),
        venue: pickFirstString(selectedEventInfo?.venue, selectedEventInfo?.place),
        prefecture_name: pickFirstString(selectedEventInfo?.prefecture_name, selectedEventInfo?.prefecture),
        city_name: pickFirstString(selectedEventInfo?.city_name, selectedEventInfo?.city),
        venue_address: pickFirstString(selectedEventInfo?.venue_address, selectedEventInfo?.address),
        memo: pickFirstString(selectedEventInfo?.memo, selectedEventInfo?.note, selectedEventInfo?.remarks),
        is_primary: true,
        members_visible_to_artists: parseVisibilityFlag(
          selectedEventInfo?.members_visible_to_artists ?? selectedEventInfo?.membersVisibleToArtists
        ),
      }];
    }, [participantSnapshot, selectedEventInfo, selectedEventId]);
    const selectedEventListRow = useMemo(() => (
      Array.isArray(events)
        ? events.find((evt) => String(evt?.event_uuid || '') === String(selectedEventId || '')) || null
        : null
    ), [events, selectedEventId]);
    const isSelectedGroupUnified = useMemo(() => {
      const selectedThreadId = resolveEventThreadId(
        selectedEventListRow || selectedEventInfo || sidebarDetails || participantSnapshot || {}
      );
      const sameThreadEventCount = selectedThreadId
        ? (Array.isArray(events)
            ? events.filter((evt) => resolveEventThreadId(evt) === selectedThreadId).length
            : 0)
        : 0;
      const chatModes = [
        resolveEventChatMode(selectedEventListRow || {}),
        resolveEventChatMode(selectedEventInfo || {}),
        resolveEventChatMode(sidebarDetails || {}),
        resolveEventChatMode(participantSnapshot || {}),
      ];
      if (chatModes.includes(CHAT_MODE_GROUP_UNIFIED)) return true;

      const groupCounts = [
        resolveEventGroupCount(selectedEventListRow || {}),
        resolveEventGroupCount(selectedEventInfo || {}),
        resolveEventGroupCount(sidebarDetails || {}),
        resolveEventGroupCount(participantSnapshot || {}),
        Number(participantGroupEvents.length || 0),
        Number(sameThreadEventCount || 0),
      ];
      return groupCounts.some((count) => Number(count) > 1);
    }, [events, selectedEventInfo, selectedEventListRow, sidebarDetails, participantSnapshot, participantGroupEvents]);
    const effectiveGroupEvents = useMemo(() => {
      if (!isSelectedGroupUnified) return participantGroupEvents;
      const normalizedPanel = normalizeGroupEventRows(groupPanelEvents, {
        includeArtistVisibility: true,
      });
      if (normalizedPanel.length > 0) {
        const visibilityMap = new Map(
          participantGroupEvents
            .filter((event) => event?.event_uuid)
            .map((event) => [String(event.event_uuid), parseVisibilityFlag(event?.members_visible_to_artists)])
        );
        return normalizedPanel.map((event) => {
          const key = String(event?.event_uuid || '');
          if (!key || !visibilityMap.has(key)) return event;
          return {
            ...event,
            members_visible_to_artists: visibilityMap.get(key) === true,
          };
        });
      }
      return participantGroupEvents;
    }, [groupPanelEvents, isSelectedGroupUnified, participantGroupEvents]);
    const groupEventDetailRows = useMemo(() => (
      buildGroupEventDetailRows({
        isSelectedGroupUnified,
        effectiveGroupEvents,
        groupEventSidebarRows,
        pickFirstString,
        toDateInput,
        formatDateJP,
        toTimeInput,
      })
    ), [effectiveGroupEvents, groupEventSidebarRows, isSelectedGroupUnified]);
    const participantGroupEventOrderMap = useMemo(() => {
      const map = new Map();
      effectiveGroupEvents.forEach((event, index) => {
        if (!event?.event_uuid) return;
        map.set(String(event.event_uuid), index);
      });
      return map;
    }, [effectiveGroupEvents]);
    const participantGroupEventMap = useMemo(() => {
      const map = new Map();
      effectiveGroupEvents.forEach((event) => {
        if (!event?.event_uuid) return;
        map.set(String(event.event_uuid), event);
      });
      return map;
    }, [effectiveGroupEvents]);
    const buildParticipantEventMembershipLabel = useCallback((eventUuids) => {
      const visibilityScopedEvents = effectiveGroupEvents.filter((event) =>
        parseVisibilityFlag(event?.members_visible_to_artists)
      );
      const targetGroupEvents =
        visibilityScopedEvents.length > 0 ? visibilityScopedEvents : effectiveGroupEvents;
      return buildEventParticipantEventMembershipLabel({
        eventUuids,
        targetGroupEvents,
        participantGroupEventOrderMap,
        participantGroupEventMap,
        pickFirstString,
      });
    }, [effectiveGroupEvents, participantGroupEventMap, participantGroupEventOrderMap]);

    const buildParticipantEventPartSummary = useCallback((artist, fallbackPartLabel = '') => {
      const normalizedFallbackPartLabel = normalizePartLabel(fallbackPartLabel) || 'パート未設定';
      return buildEventParticipantEventPartSummary({
        artist,
        fallbackPartLabel: normalizedFallbackPartLabel,
        selectedEventId,
        participantGroupEventOrderMap,
        participantGroupEventMap,
        pickFirstString,
        isUnifiedEventGroupChat: isSelectedGroupUnified,
      });
    }, [
      isSelectedGroupUnified,
      participantGroupEventMap,
      participantGroupEventOrderMap,
      selectedEventId,
    ]);

    const eventSchedule = useMemo(() => (
      buildEventScheduleFromInfo({
        selectedEventInfo,
        toDateInput,
        formatDateJP,
        toTimeInput,
      })
    ), [selectedEventInfo]);
    const eventHeaderSchedule = useMemo(() => (
      buildUnifiedEventHeaderSchedule({
        isSelectedGroupUnified,
        eventSchedule,
        groupEventSidebarRows,
        effectiveGroupEvents,
        toDateInput,
        formatDateJP,
      })
    ), [effectiveGroupEvents, eventSchedule, groupEventSidebarRows, isSelectedGroupUnified]);
    const headerPerformanceType = useMemo(() => {
      if (isSelectedGroupUnified) return '';
      return pickFirstString(
        selectedEventListRow?.performance_type,
        sidebarDetails?.performance_type,
        selectedEventInfo?.performance_type,
        participantGroupEvents[0]?.performance_type
      );
    }, [isSelectedGroupUnified, participantGroupEvents, selectedEventInfo, selectedEventListRow, sidebarDetails]);
    const headerNextPerformance = useMemo(() => (
      buildHeaderNextPerformance({
        isSelectedGroupUnified,
        groupEventDetailRows,
      })
    ), [groupEventDetailRows, isSelectedGroupUnified]);
    const isSelectedMultiPerformanceEvent = !isSelectedGroupUnified &&
      selectedEventInfo?.event_mode === 'multi_performance' &&
      Array.isArray(selectedEventInfo?.performances) &&
      selectedEventInfo.performances.length > 0;
    const headerNextMultiPerformance = useMemo(() => (
      buildHeaderNextMultiPerformance({
        isMultiPerformanceEvent: isSelectedMultiPerformanceEvent,
        performances: selectedEventInfo?.performances,
      })
    ), [isSelectedMultiPerformanceEvent, selectedEventInfo?.performances]);

    const participantCounts = useMemo(() => {
      const parse = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
      };
      const fromSnapshot = participantSnapshot?.participant_counts ?? {};
      const snapshotArtists = parse(fromSnapshot.artists);
      const snapshotAdmins = parse(fromSnapshot.admins);
      const snapshotConcierge = parse(fromSnapshot.concierge);
      const snapshotTotal = parse(fromSnapshot.total);
      if (
        snapshotArtists !== null ||
        snapshotAdmins !== null ||
        snapshotConcierge !== null ||
        snapshotTotal !== null
      ) {
        const artists = snapshotArtists ?? 0;
        const admins = snapshotAdmins ?? 0;
        const concierge = snapshotConcierge ?? 0;
        const total = snapshotTotal ?? (artists + admins + concierge);
        return { artists, admins, concierge, total };
      }

      const fromApi = selectedEventInfo?.participant_counts ?? {};
      const artists = parse(fromApi.artists) ?? 0;
      const admins = selectedEventAdmins.length;
      const concierge = isConciergeEvent && conciergePlanner ? 1 : 0;
      const totalFromApi = parse(fromApi.total);
      const totalBase = totalFromApi ?? (artists + admins + concierge);
      const total = isConciergeEvent ? totalBase : (artists + admins);
      return { artists, admins, concierge, total };
    }, [participantSnapshot, selectedEventInfo, selectedEventAdmins, conciergePlanner, isConciergeEvent]);
    const hasConciergeParticipants = useMemo(() => {
      if (Number(participantCounts.concierge || 0) > 0) return true;
      const snapshotList = Array.isArray(participantSnapshot?.concierge_planners)
        ? participantSnapshot.concierge_planners
        : [];
      if (snapshotList.length > 0) return true;
      return isConciergeEvent && !!conciergePlanner;
    }, [participantCounts.concierge, participantSnapshot, isConciergeEvent, conciergePlanner]);

    const scheduleDetail = useMemo(() => {
      const open = toTimeInput(sidebarDetails?.open_time || selectedEventInfo?.open_time) || '';
      const start = toTimeInput(sidebarDetails?.start_time || selectedEventInfo?.start_time) || '';
      return { open, start };
    }, [sidebarDetails, selectedEventInfo]);

    const [partsList, setPartsList] = useState([]);
    const [castingInfo, setCastingInfo] = useState([]);
    const [participantArtists, setParticipantArtists] = useState([]);
    const participantArtistMap = useMemo(() => {
      const map = new Map();
      participantArtists.forEach((artist) => {
        if (artist?.artist_id == null) return;
        map.set(String(artist.artist_id), artist);
      });
      return map;
    }, [participantArtists]);

    const partsLookup = useMemo(() => {
      const map = new Map();
      const list = Array.isArray(partsList) ? partsList : [];
      list.forEach((p) => {
        if (!p) return;
        const pid = Number(p.id ?? p.part_id ?? p.value);
        if (Number.isFinite(pid)) map.set(pid, p);
      });
      return map;
    }, [partsList]);

    const findPartLabelById = useCallback((partId) => {
      if (partId == null) return '';
      const num = Number(partId);
      if (Number.isFinite(num) && partsLookup.has(num)) {
        const meta = partsLookup.get(num);
        return (meta?.label ?? meta?.short_name ?? meta?.name ?? '') || '';
      }
      const s = String(partId);
      const hit =
        partsList.find((p) => String(p?.short_name) === s) ||
        partsList.find((p) => String(p?.label) === s) ||
        partsList.find((p) => String(p?.value) === s);
      return hit?.label || hit?.short_name || '';
    }, [partsList, partsLookup]);

    const findPartShortLabelById = useCallback((partId) => {
      if (partId == null) return '';
      const num = Number(partId);
      if (Number.isFinite(num) && partsLookup.has(num)) {
        const meta = partsLookup.get(num);
        return (meta?.short_name || meta?.label || meta?.name || '') || '';
      }
      return '';
    }, [partsLookup]);



    const castingInfoMap = useMemo(() => {
      const m = new Map();
      (castingInfo || []).forEach((ci) => {
        const id = ci?.id ?? ci?.casting_info_id;
        if (id == null) return;
        m.set(String(id), ci);
      });
      return m;
    }, [castingInfo]);

    const partOrderMap = useMemo(() => {
      const map = new Map();
      const list = Array.isArray(partsList) ? partsList : [];
      list.forEach((p, idx) => {
        const pid = Number(p?.id ?? p?.part_id ?? p?.value);
        if (Number.isFinite(pid) && !map.has(pid)) map.set(pid, idx);
      });
      return map;
    }, [partsList]);

    const getPartOrder = useCallback((partId) => {
      const num = Number(partId);
      if (Number.isFinite(num) && partOrderMap.has(num)) return partOrderMap.get(num);
      return 9999;
    }, [partOrderMap]);

    const venueInfo = useMemo(() => {
      const name = pickFirstString(
        sidebarDetails?.venue_name,
        sidebarDetails?.venue,
        sidebarDetails?.place,
        selectedEventInfo?.venue,
        selectedEventInfo?.venue_name
      ) || '会場未設定';
      const pref = pickFirstString(sidebarDetails?.prefecture_name, sidebarDetails?.prefecture);
      const city = pickFirstString(sidebarDetails?.city, sidebarDetails?.city_name);
      const addressLine = pickFirstString(sidebarDetails?.address, sidebarDetails?.venue_address);
      const address = [pref, city, addressLine].filter(Boolean).join(' ');
      return { name, address };
    }, [sidebarDetails, selectedEventInfo]);

    const sidebarNote = useMemo(() => (
      pickFirstString(sidebarDetails?.note, sidebarDetails?.remarks, sidebarDetails?.memo)
    ), [sidebarDetails]);

    const headerFlyerSource = useMemo(() => (
      resolveHeaderFlyerSource({
        isSelectedGroupUnified,
        headerNextPerformanceEventUuid: headerNextPerformance?.eventUuid,
        groupEventSidebarRows,
        effectiveGroupEvents,
      })
    ), [effectiveGroupEvents, groupEventSidebarRows, headerNextPerformance?.eventUuid, isSelectedGroupUnified]);
    const flyerFrontUrl = selectedEventInfo?.flyer_front_url || '';
    const flyerBackUrl = selectedEventInfo?.flyer_back_url || '';
    const selectedEventFlyerUrl = useMemo(() => {
      const key = pickFirstString(
        headerFlyerSource?.flyer_front_url,
        sidebarDetails?.flyer_front_url,
        flyerFrontUrl
      );
      if (!key) return '';
      return buildFlyerUrl(key, 'card');
    }, [flyerFrontUrl, headerFlyerSource, sidebarDetails]);
    const selectedEventFlyerBackUrl = useMemo(() => {
      const key = pickFirstString(
        headerFlyerSource?.flyer_back_url,
        sidebarDetails?.flyer_back_url,
        flyerBackUrl
      );
      if (!key) return '';
      return buildFlyerUrl(key, 'card');
    }, [flyerBackUrl, headerFlyerSource, sidebarDetails]);
    const selectedEventFlyerFullUrl = useMemo(() => {
      const key = pickFirstString(
        headerFlyerSource?.flyer_front_url,
        sidebarDetails?.flyer_front_url,
        flyerFrontUrl
      );
      if (!key) return '';
      return buildFlyerUrl(key, null);
    }, [flyerFrontUrl, headerFlyerSource, sidebarDetails]);
    const selectedEventFlyerBackFullUrl = useMemo(() => {
      const key = pickFirstString(
        headerFlyerSource?.flyer_back_url,
        sidebarDetails?.flyer_back_url,
        flyerBackUrl
      );
      if (!key) return '';
      return buildFlyerUrl(key, null);
    }, [flyerBackUrl, headerFlyerSource, sidebarDetails]);
    const eventDetailUrl = selectedEventId ? `/artist/workspaces/event-operations/events/${selectedEventId}` : '';
    const headerDetailEventId = useMemo(() => {
      if (!isSelectedGroupUnified) return selectedEventId;
      return headerNextPerformance?.eventUuid || selectedEventId;
    }, [headerNextPerformance?.eventUuid, isSelectedGroupUnified, selectedEventId]);
    const resolveVenueLink = useCallback(async (venueId) => {
      if (!venueId) return { link: null, address: '', name: '' };
      try {
        const detail = await fetchJsonSafe(`${API_URL}/api/venues/${venueId}`, {}, { label: '' });
        const venue = detail?.venue || detail || {};
        const address = [venue.prefecture, venue.city, venue.address].filter(Boolean).join(' ').trim();
        return { link: `/artist/workspaces/event-operations/venues/${venueId}`, address, name: venue?.name || '' };
      } catch {
        return { link: `/artist/workspaces/event-operations/venues/${venueId}`, address: '', name: '' };
      }
    }, []);

    const venueIdFromData =
      sidebarDetails?.venue_id ??
      selectedEventInfo?.venue_id ??
      selectedEventInfo?.venueId ??
      selectedEventInfo?.venue?.id ??
      null;

    const [venueLink, setVenueLink] = useState(null);
    const [venueResolvedAddress, setVenueResolvedAddress] = useState('');
    const [venueResolvedName, setVenueResolvedName] = useState('');

    const mapLinkUrl = useMemo(() => {
      // GoogleMap検索ワードは会場名を優先、なければ住所
      const q = venueResolvedName || venueInfo.name || venueResolvedAddress || venueInfo.address || '';
      return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : '';
    }, [venueInfo, venueResolvedAddress, venueResolvedName]);

    useEffect(() => {
      let cancelled = false;
      (async () => {
        const { link, address, name } = await resolveVenueLink(venueIdFromData);
        if (cancelled) return;
        setVenueLink(link);
        setVenueResolvedName((name || '').trim());
        const fallbackAddress = venueInfo.address || '';
        setVenueResolvedAddress(address || fallbackAddress);
      })();
      return () => { cancelled = true; };
    }, [resolveVenueLink, venueIdFromData, venueInfo.address]);

    const handleGoHeaderEventDetail = useCallback(() => {
      if (!headerDetailEventId) return;
      navigate(`/artist/workspaces/event-operations/events/${headerDetailEventId}`);
    }, [headerDetailEventId, navigate]);
    const [isUploading, setIsUploading] = useState(false);
    const [isSendLocked, setIsSendLocked] = useState(false);

    const handleUploading = useCallback((flag) => {
      if (typeof flag === 'boolean') { setIsUploading(flag); return; }
      if (flag && typeof flag === 'object') { const { active, kind } = flag; setIsUploading(!!active && kind !== 'text'); return; }
      setIsUploading(!!flag);
    }, []);
    const buildAdminSidePanelProfilePayload = useCallback(
      (source) =>
        buildEventAdminProfilePayload({
          source,
          pickFirstString,
        }),
      []
    );
    const buildConciergeSidePanelProfilePayload = useCallback(
      (source) =>
        buildEventArtistProfilePayload({
          source,
          pickFirstString,
          normalizePartLabel,
          getMainPartBadgeLabel: () => '',
          normalizeEventUuidList,
        }),
      []
    );
    const buildArtistSidePanelProfilePayload = useCallback(
      (source) =>
        buildEventArtistProfilePayload({
          source,
          pickFirstString,
          normalizePartLabel,
          getMainPartBadgeLabel: () => '',
          normalizeEventUuidList,
        }),
      []
    );
    const adminParticipants = useMemo(() => {
      return buildEventAdminParticipants({
        participantSnapshot,
        selectedEventAdmins,
        selectedEventId,
        pickFirstString,
        normalizeEventUuidList,
        buildParticipantEventMembershipLabel,
        buildAdminProfilePayload: buildAdminSidePanelProfilePayload,
        buildAvatarUrl: (avatarKey) =>
          avatarKey
            ? appendVariant(buildAssetUrl(ADMIN_PROFILE_IMAGE_PATH, avatarKey), 'medium')
            : '',
      });
    }, [
      participantSnapshot,
      selectedEventAdmins,
      selectedEventId,
      buildParticipantEventMembershipLabel,
      buildAdminSidePanelProfilePayload,
    ]);

    const conciergeParticipants = useMemo(() => {
      return buildEventConciergeParticipants({
        participantSnapshot,
        conciergePlanner,
        isConciergeEvent,
        selectedEventId,
        pickFirstString,
        normalizeEventUuidList,
        buildParticipantEventMembershipLabel,
        buildArtistProfilePayload: buildConciergeSidePanelProfilePayload,
        buildAvatarUrl: (avatarKey) =>
          avatarKey
            ? appendVariant(buildAssetUrl(ARTIST_PROFILE_IMAGE_PATH, avatarKey), 'medium')
            : '',
      });
    }, [
      participantSnapshot,
      conciergePlanner,
      isConciergeEvent,
      selectedEventId,
      buildParticipantEventMembershipLabel,
      buildConciergeSidePanelProfilePayload,
    ]);

    const visibleEffectiveGroupEvents = useMemo(() => {
      const visible = effectiveGroupEvents.filter((event) =>
        parseVisibilityFlag(event?.members_visible_to_artists)
      );
      return visible.length > 0 ? visible : effectiveGroupEvents;
    }, [effectiveGroupEvents]);
    const {
      participantPartScopeEventUuid,
      setParticipantPartScopeEventUuid,
      participantPartScopeTabs,
    } = useEventParticipantPartScope({
      effectiveGroupEvents: visibleEffectiveGroupEvents,
      selectedEventId,
      pickFirstString,
    });

    const artistParticipants = useMemo(() => {
      return buildEventArtistParticipants({
        participantSnapshot,
        participantArtists,
        participantArtistMap,
        pickFirstString,
        normalizeEventUuidList,
        participantPartScopeEventUuid,
        selectedEventId,
        effectiveGroupEvents,
        getPartOrder,
        buildParticipantEventMembershipLabel,
        buildParticipantEventPartSummary,
        buildArtistProfilePayload: buildArtistSidePanelProfilePayload,
        buildAvatarUrl: (avatarKey) =>
          avatarKey
            ? appendVariant(buildAssetUrl(ARTIST_PROFILE_IMAGE_PATH, avatarKey), 'medium')
            : '',
        resolveArtistParts: (artist) =>
          Array.isArray(artist?.artist_parts) ? artist.artist_parts : (artist?.parts || []),
        resolveFallbackPartLabel: (_artist, mainPartId) =>
          normalizePartLabel(
            findPartLabelById(mainPartId) ||
            findPartShortLabelById(mainPartId) ||
            ''
          ) || 'パート未設定',
        resolveScopedPartMeta: ({ mergedArtist, participantPartScopeEventUuid: scopedEventUuid, isAllPerformancesScope, fallbackPartLabel }) =>
          resolveEventScopedPartMeta({
            mergedArtist,
            participantPartScopeEventUuid: scopedEventUuid,
            isAllPerformancesScope,
            fallbackPartLabel,
            pickFirstString,
            normalizePartLabel,
            castingInfoMap,
          }),
        isUnifiedEventGroupChat: isSelectedGroupUnified,
      });
    }, [
      participantSnapshot,
      participantArtists,
      participantArtistMap,
      castingInfoMap,
      buildParticipantEventPartSummary,
      findPartLabelById,
      findPartShortLabelById,
      getPartOrder,
      selectedEventId,
      buildParticipantEventMembershipLabel,
      buildArtistSidePanelProfilePayload,
      effectiveGroupEvents,
      isSelectedGroupUnified,
      participantPartScopeEventUuid,
    ]);

    const artistParticipantsByPart = useMemo(() => {
      return groupEventParticipantsByPart({
        artistParticipants,
        participantPartScopeEventUuid,
        getPartOrder,
      });
    }, [artistParticipants, getPartOrder, participantPartScopeEventUuid]);

    const mentionCandidates = useMemo(() => buildChatMentionCandidates({
      admins: participantSnapshot?.admins || [],
      artists: getEventMentionArtists(participantSnapshot),
      parts: partsList,
      scope: 'event',
      currentUser: { role: 'artist', id: loggedInArtistId },
    }), [participantSnapshot, loggedInArtistId, partsList]);

    const {
      profileTarget,
      setProfileTarget,
      closeProfileModal,
      openProfileTarget,
    } = useChatProfileModalState();
    const mentionProfileTargetMap = useMemo(() => {
      const map = new Map();
      const addTarget = (role, id, payload, type) => {
        const key = String(id || '').trim();
        if (!key || !payload || map.has(`${role}:${key}`)) return;
        map.set(`${role}:${key}`, { type, payload });
      };
      adminParticipants.forEach((admin) => {
        const payload = admin?.payload;
        const fallbackId = String(admin?.key || '').replace(/^admin-/, '');
        addTarget('admin', payload?.id || payload?.admin_user_id || fallbackId, payload, 'admin');
      });
      [...artistParticipants, ...conciergeParticipants].forEach((artist) => {
        const payload = artist?.payload;
        const fallbackId = String(artist?.key || '').replace(/^(artist|concierge)-/, '');
        addTarget('artist', payload?.artist_id || payload?.id || fallbackId, payload, 'artist');
      });
      return map;
    }, [adminParticipants, artistParticipants, conciergeParticipants]);
    const handleMentionClick = useCallback((mention) => {
      const mentionType = normalizeMentionTargetType(mention);
      if (mentionType === 'part') {
        setIsMemberModalOpen(true);
        return;
      }

      const role = normalizeMentionTargetRole(mention);
      const targetId = normalizeMentionTargetId(mention);
      const target =
        mentionProfileTargetMap.get(`${role}:${targetId}`) ||
        mentionProfileTargetMap.get(`artist:${targetId}`) ||
        mentionProfileTargetMap.get(`admin:${targetId}`);
      openProfileTarget(target);
    }, [mentionProfileTargetMap, openProfileTarget]);
    const participantsPanelProps = useMemo(() => createEventParticipantsPanelProps({
      hasConciergeParticipants,
      participantCounts,
      effectiveGroupEvents,
      adminParticipants,
      conciergeParticipants,
      artistParticipantsByPart,
      participantPartScopeTabs,
      participantPartScopeEventUuid,
      onChangeParticipantPartScope: setParticipantPartScopeEventUuid,
      setProfileTarget,
      membersVisible: membersVisibleToArtists,
      hiddenArtistsText: '参加メンバー決定後公開',
    }), [
      adminParticipants,
      artistParticipantsByPart,
      conciergeParticipants,
      effectiveGroupEvents,
      hasConciergeParticipants,
      membersVisibleToArtists,
      participantCounts,
      participantPartScopeEventUuid,
      participantPartScopeTabs,
      setProfileTarget,
      setParticipantPartScopeEventUuid,
    ]);
  return {
    selectedEventAdmins,
    currentEventMembersVisibleToArtists,
    membersVisibleToArtists,
    conciergePlanner,
    adminLookup,
    isSelectedGroupUnified,
    effectiveGroupEvents,
    groupEventDetailRows,
    buildParticipantEventMembershipLabel,
    buildParticipantEventPartSummary,
    eventSchedule,
    eventHeaderSchedule,
    headerPerformanceType,
    headerNextPerformance,
    isSelectedMultiPerformanceEvent,
    headerNextMultiPerformance,
    participantCounts,
    hasConciergeParticipants,
    scheduleDetail,
    partsList,
    setPartsList,
    setCastingInfo,
    setParticipantArtists,
    participantArtistMap,
    partsLookup,
    castingInfoMap,
    venueInfo,
    sidebarNote,
    selectedEventFlyerUrl,
    selectedEventFlyerBackUrl,
    selectedEventFlyerFullUrl,
    selectedEventFlyerBackFullUrl,
    eventDetailUrl,
    venueIdFromData,
    venueLink,
    venueResolvedAddress,
    venueResolvedName,
    mapLinkUrl,
    handleGoHeaderEventDetail,
    isUploading,
    setIsUploading,
    isSendLocked,
    setIsSendLocked,
    handleUploading,
    mentionCandidates,
    profileTarget,
    closeProfileModal,
    openProfileTarget,
    handleMentionClick,
    participantsPanelProps,
  };
}
